import React, { useState } from 'react'
import { Box, Button, Chip, Collapse, Typography, useTheme } from '@mui/material'
import { KeyboardArrowDown, Payment } from '@mui/icons-material'
import { Bill } from '../../types/bills'
import { getBillingMonth, getCurrency, getShortFormDate } from '../../helpers/stringHelpers'
import { getBillItemCategory } from '../../helpers/billItems'
import { BillStatusChip } from '../../components/Payments/BillStatusChip'
import { BillLineItems } from './BillLineItems'
import { BillPaymentHistory } from './BillPaymentHistory'

interface BillingHistoryRowProps {
    bill: Bill
    onPayClick?: (bill: Bill) => void
}

export const BillingHistoryRow = ({ bill, onPayClick }: BillingHistoryRowProps) => {
    const theme = useTheme()
    const [open, setOpen] = useState(false)
    const hasDamages = (bill.bill_items || []).some(
        (item) => !item.is_voided && getBillItemCategory(item) === 'damage',
    )
    const canPay = !bill.is_paid && bill.balance_due > 0

    const handlePayClick = (event: React.MouseEvent) => {
        event.stopPropagation()
        onPayClick?.(bill)
    }

    return (
        <Box
            sx={{
                border: open
                    ? '1px solid rgba(255,255,255,0.16)'
                    : '1px solid rgba(255,255,255,0.08)',
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: open ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
                transition: 'border-color 160ms ease, background-color 160ms ease',
            }}
        >
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: { xs: 1.5, sm: 2 },
                    px: { xs: 1.75, sm: 2 },
                    py: 1.75,
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.05)',
                    },
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1,
                            mb: 0.5,
                        }}
                    >
                        <Typography
                            sx={{
                                color: 'rgba(255,255,255,0.92)',
                                fontWeight: 700,
                                fontSize: '0.98rem',
                            }}
                        >
                            {getBillingMonth(bill.billing_month)}
                        </Typography>
                        <BillStatusChip bill={bill} />
                        {hasDamages && (
                            <Chip
                                label="Damages"
                                size="small"
                                sx={{
                                    height: 22,
                                    backgroundColor: 'rgba(251,146,60,0.18)',
                                    color: '#fb923c',
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                }}
                            />
                        )}
                    </Box>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255,255,255,0.45)', display: 'block' }}
                    >
                        Due {getShortFormDate(bill.due_date)}
                        {bill.sent_at ? ` · Issued ${getShortFormDate(bill.sent_at)}` : ''}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: { xs: 'space-between', sm: 'flex-end' },
                        gap: 2,
                    }}
                >
                    <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                        <Typography
                            sx={{
                                color: canPay ? '#fb7185' : 'rgba(255,255,255,0.9)',
                                fontWeight: 800,
                                fontSize: '1.05rem',
                                lineHeight: 1.2,
                            }}
                        >
                            {getCurrency(bill.balance_due)}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255,255,255,0.42)' }}
                        >
                            {getCurrency(bill.amount_paid)} paid of {getCurrency(bill.total_amount)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {canPay && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<Payment />}
                                onClick={handlePayClick}
                                sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.78rem',
                                    px: 1.5,
                                    borderRadius: 1.5,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                Pay
                            </Button>
                        )}
                        <KeyboardArrowDown
                            sx={{
                                color: 'rgba(255,255,255,0.55)',
                                transform: open ? 'rotate(180deg)' : 'none',
                                transition: 'transform 160ms ease',
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            <Collapse in={open} timeout="auto" unmountOnExit>
                <Box
                    sx={{
                        px: { xs: 1.75, sm: 2 },
                        pb: 2,
                        pt: 0.5,
                        borderTop: '1px solid rgba(255,255,255,0.06)',
                    }}
                >
                    <BillLineItems bill={bill} />
                    <BillPaymentHistory bill={bill} />
                </Box>
            </Collapse>
        </Box>
    )
}
